import { useEffect, useState } from "react";
import { Box, Card, CardBody, CloseButton, Flex, Spinner, useToast } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router";
import CDS from "design-system";

import NoSidebarScrollablePage from "layouts/NoSidebarScrollablePage";
import { generatePath } from "helpers/url";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import FormFillView from "../Components/FormFillView";
import FormSubmittedView from "../Components/FormSubmittedView";
import { useCustomFormQuery } from "../hooks";

export default function CustomFormPreviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: form, isLoading } = useCustomFormQuery(id);
  const [showPostSubmit, setShowPostSubmit] = useState(false);

  const exitPreview = () =>
    navigate(generatePath({ path: PEOPLE_TEAM_ROUTES.customForms.show, params: { id: String(id) } }));

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitPreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const previewOnlyToast = () => {
    toast({
      title: "This is a preview",
      description: "No response was saved.",
      status: "info",
      duration: 4000,
    });
  };

  const handlePreviewSubmit = () => {
    previewOnlyToast();
    setShowPostSubmit(true);
  };

  return (
    <Flex direction="column" minH="100vh">
      <Flex
        position="sticky"
        top={0}
        zIndex="banner"
        align="center"
        px={4}
        py={2.5}
        bg="cdsIndigo.500"
      >
        <CDS.Text14 color="white" fontWeight="medium" flex={1} textAlign="center">
          Preview — this is what respondents will see. Responses won&apos;t be saved.
        </CDS.Text14>
        {/* CDS.Button doesn't forward styling props, so the white-on-dark overrides never rendered; Box carries the margin. */}
        <Box mr={3}>
          <CDS.Button
            intent={CDS.Button.intent.secondary}
            variant={CDS.Button.variant.outline}
            size="sm"
            onClick={() => setShowPostSubmit((prev) => !prev)}
          >
            {showPostSubmit ? "Back to form" : "Show post-submit screen"}
          </CDS.Button>
        </Box>
        <CloseButton aria-label="Exit preview" size="sm" color="white" onClick={exitPreview} />
      </Flex>

      <Box flex={1}>
        <NoSidebarScrollablePage>
          {isLoading || !form ? (
            <Flex justify="center" p={12}>
              {isLoading ? <Spinner /> : <CDS.Text14>Form not found.</CDS.Text14>}
            </Flex>
          ) : (
            <Card>
              <CardBody>
                {showPostSubmit ? (
                  <FormSubmittedView
                    header={form.responseScreenHeader}
                    text={form.responseScreenText}
                    onGoHome={previewOnlyToast}
                  />
                ) : (
                  <FormFillView form={form} onSubmit={handlePreviewSubmit} />
                )}
              </CardBody>
            </Card>
          )}
        </NoSidebarScrollablePage>
      </Box>
    </Flex>
  );
}
